import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
const MANAGER_ROLES = new Set(["admin", "manager", "sub_admin", "group_admin", "superadmin", "super_admin"]);

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    "x-auth-token": token,
    Authorization: token ? `Bearer ${token}` : undefined,
  };
};

export default function CommandAccessManagerModal({ open, onClose, user }) {
  const userRole = String(user?.role || "").toLowerCase();
  const canManage = MANAGER_ROLES.has(userRole);

  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedType, setSelectedType] = useState("");

  const [assignableRoles, setAssignableRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const formatApiError = (e, fallback) => {
    const body = e?.response?.data || {};
    if (Array.isArray(body.invalidRoles) && body.invalidRoles.length > 0) {
      return `${body.message || fallback} (${body.invalidRoles.join(", ")})`;
    }
    if (Array.isArray(body.invalidAllowed) && body.invalidAllowed.length > 0) {
      return `${body.message || fallback} (invalid allowed users: ${body.invalidAllowed.length})`;
    }
    if (Array.isArray(body.invalidDenied) && body.invalidDenied.length > 0) {
      return `${body.message || fallback} (invalid denied users: ${body.invalidDenied.length})`;
    }
    return body.message || e?.message || fallback;
  };

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.type === selectedType) || null,
    [templates, selectedType]
  );

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        String(u.username || "").toLowerCase().includes(q) ||
        String(u.role || "").toLowerCase().includes(q)
    );
  }, [users, search]);

  const fetchTemplates = async () => {
    if (!open || !canManage) return;
    setTemplatesLoading(true);
    setError("");
    try {
      const res = await axios.get(`${backendUrl}/commands/templates`, {
        headers: getAuthHeaders(),
      });
      const data = res?.data?.data || [];
      setTemplates(data);
      if (!selectedType && data.length > 0) {
        setSelectedType(data[0].type);
      }
    } catch (e) {
      setError(formatApiError(e, "Failed to load templates"));
    } finally {
      setTemplatesLoading(false);
    }
  };

  const fetchAssignable = async (type) => {
    if (!type || !canManage) return;
    setLoadingUsers(true);
    setError("");
    setSuccess("");
    try {
      const res = await axios.get(`${backendUrl}/commands/templates/${type}/assignable-users`, {
        headers: getAuthHeaders(),
      });
      const data = res?.data?.data || {};
      const assignable = data.assignableRoles || [];
      const templateRoles = data.template?.allowedRoles || [];
      const normalizedAssignable = new Set(assignable.map((r) => String(r).toLowerCase()));
      const safeSelected = templateRoles.filter((r) =>
        normalizedAssignable.has(String(r).toLowerCase())
      );

      setAssignableRoles(assignable);
      setSelectedRoles(safeSelected);
      setUsers(data.users || []);
    } catch (e) {
      setError(formatApiError(e, "Failed to load assignable users"));
      setUsers([]);
      setAssignableRoles([]);
      setSelectedRoles([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (open) fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canManage]);

  useEffect(() => {
    if (open && selectedType) fetchAssignable(selectedType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedType]);

  const toggleUser = (id) => {
    setUsers((prev) =>
      prev.map((u) => (String(u._id) === String(id) ? { ...u, selected: !u.selected, denied: false } : u))
    );
  };

  const onSave = async () => {
    if (!selectedType) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        allowedRoles: selectedRoles.filter((r) =>
          (assignableRoles || []).some((a) => String(a).toLowerCase() === String(r).toLowerCase())
        ),
        allowedUsers: users.filter((u) => u.selected).map((u) => u._id),
        deniedUsers: users.filter((u) => u.denied).map((u) => u._id),
      };
      const res = await axios.put(`${backendUrl}/commands/templates/${selectedType}/access`, payload, {
        headers: getAuthHeaders(),
      });
      if (res?.data?.success) {
        setSuccess(res.data.message || "Command access updated");
      } else {
        setError(res?.data?.message || "Failed to update access");
      }
    } catch (e) {
      setError(formatApiError(e, "Failed to update access"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Command Access Manager</DialogTitle>
      <DialogContent dividers>
        {!canManage ? (
          <Alert severity="warning">Only admin, manager, admin, and dealer can manage command access.</Alert>
        ) : (
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}

            <Alert severity="info">
              Template access + device assignment dono required hain. Yahan command-template level access control hota hai.
            </Alert>

            <FormControl fullWidth size="small">
              <InputLabel>Command Template</InputLabel>
              <Select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                input={<OutlinedInput label="Command Template" />}
              >
                {templates.map((tpl) => (
                  <MenuItem key={tpl.type} value={tpl.type}>
                    {tpl.name || tpl.type} ({tpl.type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {templatesLoading || loadingUsers ? (
              <Stack alignItems="center" py={3}>
                <CircularProgress size={24} />
              </Stack>
            ) : selectedTemplate ? (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Allowed Roles
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      multiple
                      value={selectedRoles}
                      onChange={(e) => setSelectedRoles(e.target.value)}
                      input={<OutlinedInput />}
                      renderValue={(selected) => (
                        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                          {selected.map((r) => (
                            <Chip key={r} size="small" label={r} />
                          ))}
                        </Stack>
                      )}
                    >
                      {assignableRoles.map((r) => (
                        <MenuItem key={r} value={r}>
                          {r}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Divider />

                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Allowed Users
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Search user/role"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </Stack>

                <List dense sx={{ maxHeight: 320, overflow: "auto", border: "1px solid #e0e0e0", borderRadius: 1 }}>
                  {filteredUsers.map((u) => (
                    <ListItem
                      key={u._id}
                      secondaryAction={
                        <Checkbox
                          edge="end"
                          checked={!!u.selected}
                          onChange={() => toggleUser(u._id)}
                        />
                      }
                    >
                      <ListItemText
                        primary={u.username}
                        secondary={`Role: ${String(u.role || "").toLowerCase()}`}
                      />
                    </ListItem>
                  ))}
                  {filteredUsers.length === 0 && (
                    <ListItem>
                      <ListItemText primary="No users in your assignable hierarchy." />
                    </ListItem>
                  )}
                </List>
              </Stack>
            ) : null}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {canManage && (
          <Button onClick={onSave} variant="contained" disabled={!selectedType || saving}>
            {saving ? "Saving..." : "Save Access"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
